import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Zap, Building, CheckCircle } from "lucide-react";

const ServicesPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      id: "diagnose",
      icon: Search,
      title: "Diagnose",
      subtitle: "Inzicht waar cash vastloopt én wat het je kost.",
      tagline: null,
      problem: "Je ziet dat cash achterblijft, maar nog niet waar het precies misgaat.\nIn deze fase maken we dat concreet.",
      approach: [
        "Analyse van je volledige service → cash keten",
        "Inzicht waar omzet vertraagt vóór en na facturatie",
        "Analyse van overdrachten en eigenaarschap",
        "Interviews met sleutelpersonen",
        "Inzicht in financiële impact per knelpunt"
      ],
      outcomes: [
        "Helder inzicht waar omzet vastloopt",
        "Overzicht van belangrijkste doorstroomlekken",
        "Concrete quick wins",
        "Inzicht in wat het je kost",
        "Duidelijke vervolgstap"
      ],
      duration: "4–6 weken",
      ideal: "Voor organisaties die eerst scherp willen krijgen waar het probleem zit.",
      resultTitle: null
    },
    {
      id: "versnelling",
      icon: Zap,
      title: "Versnelling",
      subtitle: "We doorbreken de plekken waar cash vertraagt.",
      tagline: null,
      problem: "De belangrijkste knelpunten zijn zichtbaar.\nNu zorgen we dat de doorstroom daadwerkelijk verbetert.",
      approach: [
        "Aanpak van kritieke doorstroomproblemen",
        "Aanscherpen van facturatie en opvolging",
        "Herinrichten van samenwerking tussen teams",
        "Inrichten van KPI-sturing op cash",
        "Coaching en begeleiding in de praktijk"
      ],
      outcomes: [
        "Snellere doorstroom naar betaling",
        "Duidelijk eigenaarschap in teams",
        "Concrete stuurinformatie",
        "Betere samenwerking tussen operatie en finance",
        "Direct zichtbare cashflowverbetering"
      ],
      duration: "3–6 maanden",
      ideal: "Voor organisaties die direct verbetering willen zien in cashflow.",
      resultTitle: null
    },
    {
      id: "herstructurering",
      icon: Building,
      title: "Herstructurering",
      subtitle: "We richten de organisatie zo in dat het blijft werken.",
      tagline: null,
      problem: "Als het probleem dieper zit, is versnellen niet genoeg.\nDan moet de basis opnieuw worden ingericht.",
      approach: [
        "Herontwerp van de volledige service → cash structuur",
        "Inrichten van governance en verantwoordelijkheden",
        "Implementatie van KPI's en managementritme",
        "Versterken van samenwerking tussen teams",
        "Borging van eigenaarschap en werkwijze"
      ],
      outcomes: [
        "Een organisatie ingericht op cashflow",
        "Heldere rollen en verantwoordelijkheden",
        "Structurele sturing in plaats van ad-hoc",
        "Meer voorspelbaarheid in cijfers",
        "Een team dat precies weet wat het moet doen"
      ],
      duration: "6–12 maanden",
      ideal: "Voor organisaties die structureel willen herinrichten.",
      resultTitle: null
    }
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-mist">
        <div className="container-custom">
          <div className="max-w-3xl">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Aanpak
            </motion.span>
            <motion.h1 
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="heading-1 mb-6"
              data-testid="services-headline"
            >
              Regie op Cashflow
            </motion.h1>
            <motion.p 
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="body-large mb-4"
            >
              Hoe je organisatie wordt ingericht zodat omzet daadwerkelijk doorstroomt naar cash.
            </motion.p>
            <motion.p 
              {...fadeUp}
              transition={{ delay: 0.25 }}
              className="text-navy/70"
            >
              Geen losse interventies.<br />
              Maar een gestructureerde aanpak in drie fasen: van inzicht naar structurele grip.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, idx) => (
        <section 
          key={service.id}
          id={service.id}
          className={`section-padding ${idx % 2 === 0 ? 'bg-white' : 'bg-mist'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Service Info */}
              <div>
                <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-burgundy text-white rounded-sm">
                    <service.icon size={24} strokeWidth={1} />
                  </div>
                  <div>
                    <span className="caption text-cta block">{service.subtitle}</span>
                    <h2 className="heading-3">{service.title}</h2>
                  </div>
                </motion.div>

                {service.tagline && (
                  <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="text-berry italic mb-6">
                    {service.tagline}
                  </motion.p>
                )}

                <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="mb-8">
                  <p className="body-base whitespace-pre-line">{service.problem}</p>
                </motion.div>

                <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="mb-8">
                  <h4 className="font-semibold text-navy mb-4">De aanpak:</h4>
                  <ul className="space-y-3">
                    {service.approach.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-cta rounded-full mt-2 flex-shrink-0" />
                        <span className="text-navy/70">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div {...fadeUp} transition={{ delay: 0.25 }} className="mb-8">
                  <h4 className="font-semibold text-navy mb-2">Wat dit oplevert</h4>
                  <p className="text-berry">{service.id === 'diagnose' ? 'Je weet exact waar cash vastloopt en waar je moet ingrijpen.' : service.id === 'versnelling' ? 'Cash komt sneller vrij en vertragingen verdwijnen.' : 'Structurele cashflow en blijvende grip in je organisatie.'}</p>
                </motion.div>

                <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="flex items-center gap-4 mb-8">
                  <div className="px-4 py-2 bg-burgundy/10 rounded-sm">
                    <span className="text-berry font-semibold">{service.duration}</span>
                  </div>
                  <span className="text-navy/70 text-sm">{service.ideal}</span>
                </motion.div>

                <motion.div {...fadeUp} transition={{ delay: 0.4 }}>
                  <a
                    href="https://calendly.com/rabia-regieopcashflow/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${service.id}`}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Plan een gesprek
                    <ArrowRight size={18} />
                  </a>
                </motion.div>
              </div>

              {/* Outcomes */}
              <div>
                <motion.div 
                  {...fadeUp}
                  transition={{ delay: 0.2 }}
                  className="bg-burgundy text-white p-8 md:p-12 rounded-sm h-full"
                >
                  <h3 className="font-serif text-2xl text-white mb-6">Wat je krijgt</h3>
                  <ul className="space-y-4">
                    {service.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-4">
                        <CheckCircle className="w-5 h-5 text-cta flex-shrink-0 mt-0.5" />
                        <span className="text-white/90">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Comparison Section */}
      <section className="section-padding bg-burgundy text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.span {...fadeUp} className="text-cta text-xs tracking-widest uppercase mb-4 block">
              Vergelijking
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-white mb-6">
              Je stapt in op het juiste niveau
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-white/70 text-lg max-w-2xl mx-auto mb-4">
              De aanpak is één geheel.<br />
              Afhankelijk van waar jouw organisatie nu staat, start je in de fase die het meeste impact maakt.
            </motion.p>
            <motion.p {...fadeUp} transition={{ delay: 0.25 }} className="text-white/70 text-lg max-w-2xl mx-auto">
              De meeste organisaties beginnen met Diagnose.<br />
              Van daaruit wordt duidelijk wat nodig is om door te bouwen.
            </motion.p>
          </div>

          <motion.div 
            {...fadeUp}
            transition={{ delay: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full min-w-[600px]" data-testid="services-comparison-table">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-4 text-white/60 font-normal"></th>
                  <th className="text-center py-4 text-cta font-semibold">Diagnose</th>
                  <th className="text-center py-4 text-cta font-semibold">Versnelling</th>
                  <th className="text-center py-4 text-cta font-semibold">Herstructurering</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Doorlooptijd", values: ["4-6 weken", "3-6 maanden", "6-12 maanden"] },
                  { label: "Diepte interventie", values: ["Diagnose", "Gericht", "Volledig"] },
                  { label: "Betrokkenheid MT", values: ["Beperkt", "Regelmatig", "Intensief"] },
                  { label: "Implementatie", values: ["Aanbevelingen", "Hands-on", "Transformatie"] },
                  { label: "Cultuurimpact", values: ["Awareness", "Gedrag", "Structureel"] },
                ].map((row, idx) => (
                  <tr key={row.label} className="border-b border-white/10">
                    <td className="py-4 text-white/80">{row.label}</td>
                    {row.values.map((val, i) => (
                      <td key={i} className="text-center py-4 text-white/60">{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-mist">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Volgende Stap
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="heading-2 mb-6">
              Klaar om je omzet terug te zien als cash op de bank?
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="body-large mb-4">
              In een gesprek brengen we scherp waar jouw cash vastloopt<br />
              en welke stap nu het meeste impact maakt.
            </motion.p>
            <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="mt-10">
              <a
                href="https://calendly.com/rabia-regieopcashflow/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-final-cta"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Plan een gesprek
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
